/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ChoiceGroup, IChoiceGroupOption } from '@fluentui/react/lib/ChoiceGroup';
import { DropdownMenuItemType, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { ILabelProps, Label } from '@fluentui/react/lib/Label';
import { Link } from '@fluentui/react/lib/Link';
import { IStackProps, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisDropdown, LuisDropdownProps } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { projectCreationContentLeftMargin } from 'src/customText/verticals/projectCreationModal/ProjectCreationModal';
import { QnaMakerLanguage } from 'src/qnaMaker/core/types/QnaMakerLanguages';
import { QnaMakerProjectModalFooter } from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/components/QnaMakerProjectModalFooter';
import {
    QnaMakerProjectCreationSteps,
    QnaMakerProjectDto
} from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/QnaMakerProjectCreationModal';

type QnaMakerLanguageSettings = Pick<QnaMakerProjectDto, 'language' | 'multilingualResource'>;
export type ProjectCreationModalBasicInfoProps = {
    onDismiss: () => void;
    onNext: (stepInput: Partial<QnaMakerProjectDto>) => void;
    projectDto: QnaMakerProjectDto;
    languageResource: AzureCsAccountStore;
};

const messages = defineMessages({
    title: {
        id: 'qnaMaker.qnaMakerChooseLanguageSettingScreen.title',
        defaultMessage: 'Choose language setting for resource {name}.'
    },
    description: {
        id: 'qnaMaker.qnaMakerChooseLanguageSettingScreen.description',
        defaultMessage: 'Permanently set whether or not you can create projects in multiple languages using your Azure resource {name}.'
    },
    descriptionLink: {
        id: 'qnaMaker.qnaMakerChooseLanguageSettingScreen.descriptionLink',
        defaultMessage: 'Learn more about projects in multiple languages and pricing.'
    },
    choiceGroupLabel: {
        id: 'qnaMaker.qnaMakerChooseLanguageSettingScreen.choiceGroupLabel',
        defaultMessage: 'How do you want to select the language for projects in this resource?'
    },
    choiceGroupTooltipText: {
        id: 'qnaMaker.qnaMakerChooseLanguageSettingScreen.choiceGroupTooltipText',
        defaultMessage: `If enabled, different projects can have sources in different languages. This is permanently set for all projects in this resource.

        If not enabled, sources for all projects in the resource will share the same language`
    },
    optionA: {
        id: 'qnaMaker.qnaMakerChooseLanguageSettingScreen.optionA',
        defaultMessage: 'I want to select the language when I create a project in this resource'
    },
    optionADescription: {
        id: 'qnaMaker.qnaMakerChooseLanguageSettingScreen.optionADescription',
        defaultMessage:
            'When creating a project in this resource you will be able to select what language the data is in. Selecting this option will incur more costs.'
    },
    optionALink: {
        id: 'qnaMaker.qnaMakerChooseLanguageSettingScreen.optionALink',
        defaultMessage: 'Learn more about pricing'
    },
    optionB: {
        id: 'qnaMaker.qnaMakerChooseLanguageSettingScreen.optionB',
        defaultMessage: 'I want to set the language for all projects created in this resource'
    },
    optionBDescription: {
        id: 'qnaMaker.qnaMakerChooseLanguageSettingScreen.optionBDescription',
        defaultMessage: 'All projects created in this resource will always use the same language for the data.'
    },
    languageSelectionLabel: {
        id: 'qnaMaker.qnaMakerChooseLanguageSettingScreen.languageSelectionLabel',
        defaultMessage: 'Select the language for all projects'
    },
    languageSelectionPlaceholder: {
        id: 'qnaMaker.qnaMakerChooseLanguageSettingScreen.languageSelectionPlaceholder',
        defaultMessage: 'select the language for all projects'
    },
    languageTooltipText: {
        id: 'qnaMaker.qnaMakerChooseLanguageSettingScreen.languageTooltipText',
        defaultMessage: 'This is the language that your sources will be in for all of your projects in this resource.'
    },
    chitchatSupported: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.chitchatSupported',
        defaultMessage: 'Chit-chat and extraction available'
    },
    onlyExtraction: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.onlyExtraction',
        defaultMessage: 'Only extraction available'
    }
});

const { styleComponent, getClassName } = getStylistV2('QnaMakerChooseLanguageSettingScreen');
const StyledDropdown = styleComponent<LuisDropdownProps>(LuisDropdown)('StyledDropdown', { width: '300px', maxHeight: '300px' });
const StyledLabel = styleComponent<ILabelProps>(Label)('StyledLabel', { display: 'flex' });
const OptionWrapper = styleComponent<IStackProps>(Stack)('OptionWrapper', { paddingLeft: '30px', paddingBottom: '10px' });

const calloutIconClassName = getClassName('calloutIconClassName', {
    fontSize: '14px',
    lineHeight: '14px'
});

export const getProjectSupportedLanguageDropdownOptions = (
    localize: (
        messageDescriptor: ReactIntl.FormattedMessage.MessageDescriptor,
        values?: {
            [key: string]: ReactIntl.MessageValue;
        }
    ) => string
) => [
    { key: 'chitchatSupportedLanguages', text: localize(messages.chitchatSupported), itemType: DropdownMenuItemType.Header },
    { key: 'chitchatSupportedLanguagesDivider', text: '', itemType: DropdownMenuItemType.Divider },
    ...LuisConstants.QnaMaker.SupportedProjectLangues.filter(l => l.isChitchatSupported).map(c => ({ key: c.code, text: c.name })),
    { key: 'extractionOnlyLanguages', text: localize(messages.onlyExtraction), itemType: DropdownMenuItemType.Header },
    { key: 'extractionOnlyLanguagesDivider', text: '', itemType: DropdownMenuItemType.Divider },
    ...LuisConstants.QnaMaker.SupportedProjectLangues.filter(l => !l.isChitchatSupported).map(c => ({ key: c.code, text: c.name }))
];

export const QnaMakerChooseLanguageSettingScreen = observer((props: ProjectCreationModalBasicInfoProps) => {
    const { languageResource, projectDto, onDismiss, onNext } = props;
    const { localize } = useLocalization();

    const [languageSettings, setLanguageSettings] = useUpdateState<QnaMakerLanguageSettings>({
        multilingualResource: projectDto.multilingualResource,
        language: projectDto.language
    });
    const isModalValid = Boolean(languageSettings.multilingualResource || languageSettings.language);

    const languagesOptions: IDropdownOption[] = getProjectSupportedLanguageDropdownOptions(localize);

    const labelRenderer = (label: string, tooltipText: string | React.ReactNode, isRequired: boolean = true) => (
        <Stack horizontal verticalAlign="center" grow>
            <StyledLabel styles={{ root: { selectors: { '::after': { paddingRight: 0 } } } }} required={isRequired}>
                {label}
            </StyledLabel>
            <CalloutButton content={tooltipText} iconClassName={calloutIconClassName} />
        </Stack>
    );

    const choiceGroupOptions: IChoiceGroupOption[] = [
        {
            key: 'multilingualResource',
            text: localize(messages.optionA),
            onRenderLabel: () => (
                <OptionWrapper tokens={{ childrenGap: 8 }} data-automation-id="multilingualResourceRadio">
                    <LuisText variant="medium">{localize(messages.optionA)}</LuisText>
                    <StackItem>
                        <LuisText variant="small">
                            {localize(messages.optionADescription)}
                            &nbsp;
                            <Link href="https://aka.ms/qa-language-pricing">{localize(messages.optionALink)}</Link>
                        </LuisText>
                    </StackItem>
                </OptionWrapper>
            )
        },
        {
            key: 'singleLanguageResource',
            text: localize(messages.optionB),
            onRenderLabel: () => (
                <OptionWrapper tokens={{ childrenGap: 8 }}>
                    <LuisText variant="medium">{localize(messages.optionB)}</LuisText>
                    <StackItem>
                        <LuisText variant="small">{localize(messages.optionBDescription)}</LuisText>
                    </StackItem>
                    <StyledDropdown
                        isSearchEnabled
                        maxHeight={200}
                        orderByKey="none"
                        options={languagesOptions}
                        selectedKey={languageSettings.language}
                        disabled={languageSettings.multilingualResource}
                        label={localize(messages.languageSelectionLabel)}
                        placeholder={localize(messages.languageSelectionPlaceholder)}
                        onChange={(_, option) => setLanguageSettings({ language: option.key as QnaMakerLanguage })}
                        onRenderLabel={() =>
                            labelRenderer(
                                localize(messages.languageSelectionLabel),
                                localize(messages.languageTooltipText),
                                !languageSettings.multilingualResource
                            )
                        }
                    />
                </OptionWrapper>
            )
        }
    ];

    return (
        <Stack>
            <Stack
                styles={{ root: { height: '500px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 24, padding: `0 0 0 ${projectCreationContentLeftMargin}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title, { name: languageResource.name })}
                    </LuisText>
                    <LuisText>
                        {localize(messages.description, { name: languageResource.name })}
                        &nbsp;
                        <Link href="https://aka.ms/question_answering_languages">{localize(messages.descriptionLink)}</Link>
                    </LuisText>
                </Stack>

                <Stack tokens={{ childrenGap: 6 }}>
                    {labelRenderer(localize(messages.choiceGroupLabel), localize(messages.choiceGroupTooltipText), true)}

                    <ChoiceGroup
                        options={choiceGroupOptions}
                        selectedKey={languageSettings.multilingualResource ? 'multilingualResource' : 'singleLanguageResource'}
                        onChange={(_, option) => setLanguageSettings({ multilingualResource: option.key === 'multilingualResource' })}
                    />
                </Stack>
            </Stack>

            <QnaMakerProjectModalFooter
                isValid={isModalValid}
                onDismiss={onDismiss}
                onNext={() => onNext(languageSettings)}
                activeStep={QnaMakerProjectCreationSteps.EnterBasicInformation}
            />
        </Stack>
    );
});
